/**
 * AP-39 Nachtrag 2 (3., 4.) — **die Antwort des Lieferanten im Raster der Positionen.**
 *
 * Dieselben Spalten in derselben Reihenfolge wie das Positionsraster (`PositionGrid`): Pos · Typ · Menge ·
 * Einh. · Bezeichnung · Artikel · EP · PE · Rabatt % · Betrag — statt USt % die Lieferzeit (Angebot) bzw. der
 * Liefertermin (Bestätigung). Was im Raster keine eigene Spalte hat, steht klein **unter** dem Feld, zu dem es
 * gehört — wie Alt.-Menge und Langtext im Raster: Alternative und Bemerkung unter der Bezeichnung, die
 * Artikelnummer des Lieferanten unter unserer, „je" unter dem Einzelpreis (PE-105).
 *
 * Eine eigene Tabelle über den Positionen, nicht Teil des Rasters (PE-104): Die Antwort speichert **feldweise**
 * (AP-36, eine Speicherstelle je Zeile) und bleibt an einem festgeschriebenen Beleg erfassbar; die Positionen
 * speichern als Ganzes mit „Speichern".
 *
 * Farbe (PE-106): Was gilt, steht grün; was die KI gelesen hat und noch nicht gilt, violett in einer Zeile
 * darunter — in denselben Spalten (PE-107); unsicher Gelesenes gelb.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, CheckCircle2, Plus, Sparkles, Trash2 } from 'lucide-react'
import {
  antwortNetto,
  geldText,
  preisText,
  type DocumentLineResponseView,
  type DocumentResponseKind,
  type DocumentResponseLineView,
  type DocumentResponseSuggestion,
  type DocumentResponseView,
} from '@symphenia/shared-types'
import { ZELLE_RAHMEN } from '@/components/zeilenblock'
import { GeldFeldFuer, geldEingabe } from '@/components/geldfeld'
import { kiFarbe, kiGelb } from '@/components/ki-vorschlag/KiVorschlag'
import { fmtDate } from '@/lib/format-date'
import { FeldStatus, FeldStatusZeichen, datumOhneVorlage, useFeld, useSpeicherstelle, useStellenDaten, type Datensatz, type Speicherstelle, type Status } from '@/lib/speichern'
import { responsesApi } from '../api/documents.api'
import type { UnitRef } from '../types/document.types'

export const mengeText = (x: number) => x.toLocaleString('de-DE', { maximumFractionDigits: 3 })

/** PE-106: die Farbe der Antwort des Lieferanten — sie gilt. Eine Stelle für Zeile und Rand. */
export const ANTWORT_FARBE = { zeile: 'bg-emerald-500/[0.07]', rand: 'bg-emerald-500/70' } as const
/** Gelesen, gilt noch nicht — die Farbe des KI-Vorschlags (AP-39 Nachtrag 1, PE-99). */
const GELESEN = { zeile: 'bg-violet-500/[0.07]', rand: 'bg-violet-500/70' } as const

const SICHER_KLASSE: Record<string, string> = {
  hoch: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  mittel: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  niedrig: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
}

/** Felder wie im Raster (AP-32 N-88): Rand und Fläche, gesperrt wie Text. */
const zelle = `${ZELLE_RAHMEN} px-1.5 py-1 text-sm`
/** Das Nebenfeld klein unter dem Hauptfeld — wie Alt.-Menge und Alt.-Einheit im Raster. */
const klein = `${ZELLE_RAHMEN} mt-1 px-1.5 py-0 text-xs text-muted-foreground`
/** Ein Wert ohne Feld (aus der Belegzeile) — derselbe Kasten wie ein gesperrtes Feld, damit die Zeilen fluchten. */
const wert = 'block min-h-[calc(1.75rem+2px)] border border-transparent px-1.5 py-1'
/** Der Betrag: rechtsbündig in seiner Zelle, auf der Höhe der Felder. */
const betragWert = 'block min-h-[calc(1.75rem+2px)] border border-transparent py-1'
/** Klein unter einem Feld: bestellt, Wunschtermin, netto je Einheit. */
const darunter = 'mt-1 block px-1.5 text-xs text-muted-foreground'

/**
 * Die Wörter des Abschnitts je Art: An einer Bestellung heißt die Antwort „Bestätigung", die Nummer
 * „AB-Nummer". Was die Bestätigung nicht eigens benennt, heißt wie bei der Antwort.
 */
export function useTexte(kind: DocumentResponseKind) {
  const { t, i18n } = useTranslation()
  return (key: string, opts?: Record<string, unknown>): string =>
    kind === 'CONFIRMATION' && i18n.exists(`documents.confirmation.${key}`) ? t(`documents.confirmation.${key}`, opts) : t(`documents.response.${key}`, opts)
}

/** Eine Zeile ohne Antwort: die Felder leer, ohne Stand — der erste PATCH legt sie an. */
const LEER: Datensatz = {
  unitPrice: null,
  priceUnit: 1,
  discountPercent: null,
  unitCode: null,
  quantity: null,
  deliveryAt: null,
  leadTimeDays: null,
  notOffered: false,
  alternative: null,
  note: null,
  articleNumber: null,
}

/** Der Stand einer Zeile aus ihren Feldern: ein Fehler zuerst, dann „speichert", dann „gespeichert". */
function zeilenStatus(felder: readonly Status[]): Status {
  const fehler = felder.find((f) => f.fehler)
  if (fehler) return fehler
  if (felder.some((f) => f.zustand === 'speichert')) return { zustand: 'speichert', fehler: null }
  if (felder.some((f) => f.zustand === 'gespeichert')) return { zustand: 'gespeichert', fehler: null }
  return { zustand: 'ruhig', fehler: null }
}

/** Der linke Rand: die Farbe der Zeile als Streifen — an der Stelle, an der das Raster markiert. */
function Rand({ farbe }: { farbe: string }) {
  return (
    <td className="relative w-2 p-0">
      <span className={`absolute inset-y-0 left-0 w-1 ${farbe}`} aria-hidden />
    </td>
  )
}

export function SupplierResponseGrid({
  documentId,
  kind,
  daten,
  darf,
  preise,
  einheiten,
  haken,
  onHaken,
  onGespeichert,
  onZusatzAnlegen,
  onZusatzLoeschen,
  laeuft,
}: {
  documentId: string
  kind: DocumentResponseKind
  daten: DocumentResponseView
  darf: boolean
  preise: boolean
  einheiten: UnitRef[]
  /** angehakte Vorschläge (Kennung der Antwortzeile) — „Bestätigen" steht über der Tabelle */
  haken: ReadonlySet<string>
  onHaken: (responseLineId: string, an: boolean) => void
  onGespeichert: () => void
  onZusatzAnlegen: (label: string) => Promise<void>
  onZusatzLoeschen: (responseLineId: string) => void
  laeuft: boolean
}) {
  const t = useTexte(kind)
  const { t: tg } = useTranslation()
  const bestaetigung = kind === 'CONFIRMATION'
  const [neu, setNeu] = useState('')
  // Rand · Pos · Typ · Menge · Einh. · Bezeichnung · Artikel · (EP · PE · Rabatt · Betrag) · Frist · Schalter
  const spalten = 9 + (preise ? 4 : 0)

  return (
    <div className="overflow-x-auto rounded-md border border-border bg-card">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/50 text-xs text-muted-foreground">
          {/* Die Köpfe des Positionsrasters, in seiner Reihenfolge (PE-105). */}
          <tr className="h-8 align-middle [&>th]:whitespace-nowrap">
            <th className="w-2 p-0" />
            <th className="w-14 px-1.5 text-left">{tg('documents.grid.pos')}</th>
            <th className="w-24 px-1.5 text-left">{tg('documents.grid.type')}</th>
            <th className="w-20 px-1.5 text-right">{bestaetigung ? t('quantity') : tg('documents.grid.quantity')}</th>
            <th className="w-16 px-1.5 text-left">{tg('documents.grid.unit')}</th>
            <th className="min-w-[16rem] px-1.5 text-left">{tg('documents.grid.name')}</th>
            <th className="w-28 px-1.5 text-left">{tg('documents.grid.articleNumber')}</th>
            {preise && (
              <>
                <th className="w-24 px-1.5 text-right">{tg('documents.grid.unitPrice')}</th>
                <th className="w-20 px-1.5 text-right">{tg('documents.grid.priceUnit')}</th>
                <th className="w-16 px-1.5 text-right">{tg('documents.grid.discount')}</th>
                <th className="w-28 px-2 text-right">{tg('documents.grid.amount')}</th>
              </>
            )}
            <th className={`${bestaetigung ? 'w-36' : 'w-20'} px-1.5 text-right`}>{bestaetigung ? t('deliveryAt') : t('leadTimeShort')}</th>
            <th className="w-12 px-1.5" />
          </tr>
        </thead>
        <tbody>
          {daten.lines.map((l) => (
            <AntwortZeile
              // Schlüssel ist die Belegzeile, nicht die Antwort: Die erste Eingabe legt die Antwort an — ein
              // neuer Schlüssel würde die Zeile neu aufbauen und dem Benutzer den Fokus nehmen.
              key={l.lineId}
              documentId={documentId}
              kind={kind}
              zeile={l}
              darf={darf}
              preise={preise}
              einheiten={einheiten}
              haken={!!l.response && haken.has(l.response.id)}
              onHaken={(an) => l.response && onHaken(l.response.id, an)}
              onGespeichert={onGespeichert}
            />
          ))}
          {daten.extra.map((z) => (
            <ZusatzZeile
              key={z.id}
              documentId={documentId}
              kind={kind}
              zeile={z}
              darf={darf}
              preise={preise}
              haken={haken.has(z.id)}
              onHaken={(an) => onHaken(z.id, an)}
              onGespeichert={onGespeichert}
              onLoeschen={() => onZusatzLoeschen(z.id)}
            />
          ))}
          {daten.lines.length === 0 && daten.extra.length === 0 && (
            <tr>
              <td colSpan={spalten} className="px-4 py-6 text-center text-sm text-muted-foreground">
                {t('noLines')}
              </td>
            </tr>
          )}
        </tbody>
        {preise && !!daten.total && (
          <tfoot>
            <tr className="border-t border-border bg-muted/30">
              <td colSpan={7} className="px-1.5 py-1.5 text-right text-xs text-muted-foreground">
                {t('totalFoot')}
              </td>
              <td colSpan={3} />
              <td className="px-2 py-1.5 text-right font-semibold tabular-nums">{geldText(daten.total)}</td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        )}
      </table>
      {/* Wo das Raster „+ Position" hat: eine zusätzliche Zeile anlegen. */}
      {darf && (
        <form
          className="flex flex-wrap items-center gap-2 border-t border-border bg-muted/20 px-2 py-1.5 text-xs"
          onSubmit={(e) => {
            e.preventDefault()
            const label = neu.trim()
            if (!label) return
            void onZusatzAnlegen(label).then(() => setNeu(''))
          }}
        >
          <span className="text-muted-foreground">{t('extraTitle')}:</span>
          <span className="w-72 max-w-full">
            <input className={`${zelle} text-xs`} placeholder={t('extraPlaceholder')} value={neu} onChange={(e) => setNeu(e.target.value)} aria-label={t('extraPlaceholder')} />
          </span>
          <button type="submit" disabled={!neu.trim() || laeuft} className="inline-flex items-center gap-1 rounded border border-input px-2 py-0.5 hover:bg-muted disabled:opacity-50">
            <Plus className="h-3 w-3" /> {t('extraAdd')}
          </button>
        </form>
      )}
    </div>
  )
}

/** Die Felder einer Antwortzeile — dieselben an einer Belegzeile und an einer zusätzlichen Zeile. */
function useAntwortFelder(kind: DocumentResponseKind, stelle: Speicherstelle<unknown>, d: DocumentLineResponseView | undefined, darf: boolean, preise: boolean, einheitAlsText = false) {
  const t = useTexte(kind)
  const nichtAngeboten = useFeld<boolean>(stelle, 'notOffered', { art: 'schalter', label: t('notOffered'), gesperrt: !darf })
  const aus = !darf || Boolean(d?.notOffered)
  return {
    nichtAngeboten,
    // Wie im Raster `1.234,50` (geldbetraege.md); ein Lieferant bietet auch mit vier Stellen an (0,0385).
    ep: useFeld<number | null>(stelle, 'unitPrice', { art: 'zahl', stellen: 4, schreib: (w) => geldEingabe(w, 4), label: t('unitPrice'), gesperrt: aus || !preise }),
    pe: useFeld<number | null>(stelle, 'priceUnit', {
      art: 'zahl',
      stellen: 0,
      label: t('priceUnit'),
      pflicht: true,
      gesperrt: aus || !preise,
      pruefe: (v) => (v !== null && v >= 1 ? null : t('priceUnitMin')),
    }),
    rabatt: useFeld<number | null>(stelle, 'discountPercent', { art: 'zahl', stellen: 2, label: t('discount'), gesperrt: aus || !preise }),
    // An einer Belegzeile eine Auswahl (speichert sofort), an einer zusätzlichen Zeile ihre Einheit als Text.
    je: useFeld<string | null>(stelle, 'unitCode', einheitAlsText ? { label: t('unit'), gesperrt: !darf } : { art: 'auswahl', label: t('per'), gesperrt: aus || !preise }),
    lieferzeit: useFeld<number | null>(stelle, 'leadTimeDays', { art: 'zahl', stellen: 0, label: t('leadTime'), gesperrt: aus }),
    alternative: useFeld<string | null>(stelle, 'alternative', { label: t('alternative'), gesperrt: !darf }),
    artikel: useFeld<string | null>(stelle, 'articleNumber', { label: t('supplierArticle'), gesperrt: !darf }),
    notiz: useFeld<string | null>(stelle, 'note', { label: t('note'), gesperrt: !darf }),
    // Bestätigung (11.1): bestätigte Menge und Liefertermin; an einer zusätzlichen Zeile ihre Menge.
    menge: useFeld<number | null>(stelle, 'quantity', { art: 'zahl', stellen: 3, label: t('quantity'), gesperrt: !darf }),
    termin: useFeld<string | null>(stelle, 'deliveryAt', { art: 'datum', label: t('deliveryAt'), gesperrt: !darf }),
  }
}

/** Die Antwort an einer Belegzeile — eine Speicherstelle je Zeile (AP-36 7.2). */
function AntwortZeile({
  documentId,
  kind,
  zeile,
  darf,
  preise,
  einheiten,
  haken,
  onHaken,
  onGespeichert,
}: {
  documentId: string
  kind: DocumentResponseKind
  zeile: DocumentResponseLineView
  darf: boolean
  preise: boolean
  einheiten: UnitRef[]
  haken: boolean
  onHaken: (an: boolean) => void
  onGespeichert: () => void
}) {
  const t = useTexte(kind)
  const { t: tg } = useTranslation()
  const bestaetigung = kind === 'CONFIRMATION'
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `docResponseLine:${kind}:${zeile.lineId}`,
    bezeichnung: zeile.name,
    daten: (zeile.response ?? LEER) as unknown as Datensatz,
    schreibe: async (patch, stand) => {
      const r = await responsesApi.line(documentId, kind, zeile.lineId, patch, stand)
      onGespeichert()
      return (r.data ?? LEER) as unknown as Datensatz
    },
    keepalive: { url: `${responsesApi.url(documentId, kind)}/lines/${zeile.lineId}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>
  const d = useStellenDaten(stelle) as unknown as DocumentLineResponseView
  const f = useAntwortFelder(kind, stelle, d, darf, preise)
  const status = zeilenStatus([f.nichtAngeboten, f.ep, f.pe, f.rabatt, f.je, f.lieferzeit, f.alternative, f.artikel, f.notiz, f.menge, f.termin])
  // Eine Hauptposition steht fett wie im Raster; eine Unterposition („1.2") nicht.
  const haupt = !zeile.positionLabel.includes('.')
  const nichtAngeboten = !bestaetigung && Boolean(f.nichtAngeboten.wert)
  // AP-37 O 7.4a: Der Preis gilt je anderer Einheit (Kilopreis auf Meter) — ohne Menge in dieser Einheit kein Betrag.
  const andereEinheit = !bestaetigung && !!f.je.wert && f.je.wert !== zeile.unitCode
  const einheitDerAntwort = (f.je.wert as string | null) || zeile.unitCode || t('unit')
  // Der Betrag rechnet beim Tippen mit (KA-51) — mit dem, was in den Feldern steht.
  const betrag = (() => {
    if (!preise) return null
    if (bestaetigung) {
      // Was der Lieferant nicht nennt, gilt wie bestellt (PE-64).
      if (!zeile.response) return null
      const netto =
        f.ep.wert != null ? antwortNetto(f.ep.wert, f.pe.wert ?? 1, f.rabatt.wert) : zeile.orderUnitPrice != null ? antwortNetto(zeile.orderUnitPrice, zeile.orderPriceUnit ?? 1, zeile.orderDiscountPercent) : null
      return netto === null ? null : (f.menge.wert ?? zeile.quantity) * netto
    }
    if (nichtAngeboten || f.ep.wert == null || andereEinheit) return null
    return zeile.quantity * antwortNetto(f.ep.wert, f.pe.wert ?? 1, f.rabatt.wert)
  })()
  const netto = preise && !bestaetigung && !nichtAngeboten && f.ep.wert != null && ((f.pe.wert ?? 1) > 1 || !!f.rabatt.wert || andereEinheit) ? antwortNetto(f.ep.wert, f.pe.wert ?? 1, f.rabatt.wert) : null
  const abweichungen = (zeile.deviations ?? []).filter((a) => a.kind !== 'MISSING')
  const bestaetigt = !!zeile.response?.confirmedAt
  const vorschlag = preise ? (zeile.response?.suggested ?? null) : null

  return (
    <>
      <tr className={`border-b border-border/60 align-top ${ANTWORT_FARBE.zeile}`} data-antwort-zeile={zeile.lineId}>
        <Rand farbe={ANTWORT_FARBE.rand} />
        <td className="px-1.5 py-1 font-mono text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-0.5 py-1">
            <span className="inline-block w-3.5" />
            {zeile.positionLabel}
          </span>
        </td>
        <td className="px-1.5 py-1">
          <span className={`${wert} text-muted-foreground`}>{tg('documents.lineTypes.ARTICLE')}</span>
        </td>
        <td className="px-1.5 py-1">
          {bestaetigung ? (
            <>
              <input className={`${zelle} text-right tabular-nums`} inputMode="decimal" placeholder={mengeText(zeile.quantity)} aria-label={t('quantity')} {...f.menge.props} />
              <span className={`${darunter} text-right`}>
                {t('ordered')} {mengeText(zeile.quantity)}
              </span>
            </>
          ) : (
            <span className={`${wert} text-right tabular-nums`}>{mengeText(zeile.quantity)}</span>
          )}
        </td>
        <td className="px-1.5 py-1">
          <span className={wert}>{zeile.unitCode ?? ''}</span>
        </td>
        <td className="px-1.5 py-1">
          <span className={`${wert} ${haupt ? 'font-semibold' : ''} ${nichtAngeboten ? 'text-muted-foreground line-through' : ''}`}>{zeile.name}</span>
          {!bestaetigung && (
            <input className={`${zelle} mt-1 text-xs`} placeholder={`${t('alternative')} — ${t('alternativeHint')}`} title={t('alternativeHint')} aria-label={t('alternative')} {...f.alternative.props} />
          )}
          <input className={`${zelle} mt-1 text-xs`} placeholder={t('note')} aria-label={t('note')} {...f.notiz.props} />
          {bestaetigung ? (
            // 11.2 — Stand der Zeile, die Abweichungen und „wie bestellt".
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {bestaetigt && abweichungen.length === 0 && (
                <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> {t('states.CONFIRMED')}
                </span>
              )}
              {abweichungen.map((a) => (
                <span key={a.kind} className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                  <AlertTriangle className="h-3 w-3" aria-hidden /> {a.text}
                </span>
              ))}
              {!bestaetigt && darf && (
                <button type="button" className="rounded-md border border-border px-2 py-0.5 text-foreground hover:bg-muted" onClick={() => void responsesApi.asOrdered(documentId, [zeile.lineId]).then(onGespeichert)}>
                  {t('asOrdered')}
                </button>
              )}
            </div>
          ) : (
            // Wie „Option" und „Alternative" im Raster: das Kästchen unter dem Text.
            <div className="mt-0.5 flex flex-wrap gap-3 text-xs text-muted-foreground">
              <label className="flex items-center gap-1">
                <input type="checkbox" {...f.nichtAngeboten.schalter} />
                {t('notOffered')}
              </label>
            </div>
          )}
          {status.fehler && <FeldStatus status={status} zeichen={false} />}
        </td>
        <td className="px-1.5 py-1">
          <span className={`${wert} font-mono`}>{zeile.articleNumber ?? ''}</span>
          {!bestaetigung && <input className={klein} placeholder={t('supplierArticleShort')} title={t('supplierArticle')} aria-label={t('supplierArticle')} {...f.artikel.props} />}
        </td>
        {preise && (
          <>
            <td className="px-1.5 py-1">
              {/* Wie das E-Preis-Feld im Raster: ohne Fokus `1.234,50` (N-91). */}
              <GeldFeldFuer feld={f.ep.props} stellen={4} className={zelle} aria-label={t('unitPrice')} />
              {bestaetigung ? (
                zeile.orderUnitPrice != null && (
                  <span className={`${darunter} text-right tabular-nums`}>
                    {t('ordered')} {preisText(zeile.orderUnitPrice)}
                    {(zeile.orderPriceUnit ?? 1) > 1 ? ` / ${zeile.orderPriceUnit}` : ''}
                    {zeile.orderDiscountPercent ? ` − ${zeile.orderDiscountPercent.toLocaleString('de-DE')} %` : ''}
                  </span>
                )
              ) : (
                <select className={klein} title={t('perUnitTitle')} aria-label={t('per')} {...f.je.props}>
                  <option value="">{`${t('per')} ${zeile.unitCode ?? t('unit')}`}</option>
                  {einheiten
                    .filter((e) => e.code !== zeile.unitCode)
                    .map((e) => (
                      <option key={e.code} value={e.code}>
                        {`${t('per')} ${e.code}`}
                      </option>
                    ))}
                  {/* Eine gelesene Einheit, die der Mandant nicht in seiner Liste hat, bleibt wählbar. */}
                  {d?.unitCode && d.unitCode !== zeile.unitCode && !einheiten.some((e) => e.code === d.unitCode) && <option value={d.unitCode}>{`${t('per')} ${d.unitCode}`}</option>}
                </select>
              )}
            </td>
            <td className="px-1.5 py-1">
              <input className={`${zelle} text-right tabular-nums`} inputMode="numeric" aria-label={t('priceUnit')} {...f.pe.props} />
            </td>
            <td className="px-1.5 py-1">
              <input className={`${zelle} text-right tabular-nums`} inputMode="decimal" aria-label={t('discount')} {...f.rabatt.props} />
            </td>
            <td className="px-2 py-1 text-right tabular-nums">
              <span className={betragWert}>
                {andereEinheit ? (
                  <span className="text-muted-foreground" title={t('otherUnitAmount')}>
                    —
                  </span>
                ) : betrag !== null ? (
                  <span className="font-semibold">{geldText(betrag)}</span>
                ) : (
                  ''
                )}
              </span>
              {netto !== null && <span className="mt-1 block text-xs text-muted-foreground">{t('netPerUnit', { price: preisText(netto), unit: einheitDerAntwort })}</span>}
            </td>
          </>
        )}
        <td className="px-1.5 py-1">
          {bestaetigung ? (
            <>
              <input type="date" className={`${zelle} text-xs ${datumOhneVorlage(f.termin.props.value)}`} aria-label={t('deliveryAt')} {...f.termin.props} />
              {zeile.orderDeliveryDate && (
                <span className={darunter}>
                  {t('wish')} {fmtDate(zeile.orderDeliveryDate)}
                </span>
              )}
            </>
          ) : (
            <input className={`${zelle} text-right tabular-nums`} inputMode="numeric" placeholder={t('daysPlaceholder')} title={t('leadTime')} aria-label={t('leadTime')} {...f.lieferzeit.props} />
          )}
        </td>
        <td className="px-1.5 py-2 text-right">
          <FeldStatusZeichen status={status} />
        </td>
      </tr>
      {vorschlag && <GelesenZeile kind={kind} v={vorschlag} menge={zeile.quantity} einheit={zeile.unitCode} preise={preise} haken={haken} darf={darf} onHaken={onHaken} />}
    </>
  )
}

/** Eine zusätzliche Zeile — der Lieferant bietet oder bestätigt etwas, das nicht im Beleg steht. */
function ZusatzZeile({
  documentId,
  kind,
  zeile,
  darf,
  preise,
  haken,
  onHaken,
  onGespeichert,
  onLoeschen,
}: {
  documentId: string
  kind: DocumentResponseKind
  zeile: DocumentLineResponseView
  darf: boolean
  preise: boolean
  haken: boolean
  onHaken: (an: boolean) => void
  onGespeichert: () => void
  onLoeschen: () => void
}) {
  const t = useTexte(kind)
  const bestaetigung = kind === 'CONFIRMATION'
  const url = `${responsesApi.url(documentId, kind)}/extra/${zeile.id}`
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `docResponseExtra:${zeile.id}`,
    bezeichnung: zeile.label ?? '',
    daten: zeile as unknown as Datensatz,
    schreibe: async (patch, stand) => {
      const r = await responsesApi.extra(documentId, kind, zeile.id, patch, stand)
      onGespeichert()
      return (r.data ?? {}) as unknown as Datensatz
    },
    keepalive: { url, method: 'PATCH' },
  }) as Speicherstelle<unknown>
  const d = useStellenDaten(stelle) as unknown as DocumentLineResponseView
  const f = useAntwortFelder(kind, stelle, d, darf, preise, true)
  const bezeichnung = useFeld<string | null>(stelle, 'label', { label: t('extraLabel'), pflicht: true, gesperrt: !darf })
  const status = zeilenStatus([bezeichnung, f.menge, f.je, f.ep, f.pe, f.rabatt, f.lieferzeit, f.termin, f.artikel, f.notiz])
  const betrag = preise && f.ep.wert != null && f.menge.wert != null ? f.menge.wert * antwortNetto(f.ep.wert, f.pe.wert ?? 1, f.rabatt.wert) : null

  return (
    <>
      <tr className={`border-b border-border/60 align-top ${ANTWORT_FARBE.zeile}`} title={t('extraRowsHint')} data-antwort-zusatz={zeile.id}>
        <Rand farbe={ANTWORT_FARBE.rand} />
        <td className="px-1.5 py-1 font-mono text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-0.5 py-1">
            <span className="inline-block w-3.5" />·
          </span>
        </td>
        <td className="px-1.5 py-1">
          <span className={`${wert} text-muted-foreground`}>{t('typeExtra')}</span>
        </td>
        <td className="px-1.5 py-1">
          <input className={`${zelle} text-right tabular-nums`} inputMode="decimal" aria-label={t('quantity')} {...f.menge.props} />
        </td>
        <td className="px-1.5 py-1">
          <input className={zelle} aria-label={t('unit')} {...f.je.props} />
        </td>
        <td className="px-1.5 py-1">
          <input className={`${zelle} font-semibold`} aria-label={t('extraLabel')} {...bezeichnung.props} />
          <input className={`${zelle} mt-1 text-xs`} placeholder={t('note')} aria-label={t('note')} {...f.notiz.props} />
          {status.fehler && <FeldStatus status={status} zeichen={false} />}
        </td>
        <td className="px-1.5 py-1">
          {/* Eine zusätzliche Zeile hat keinen eigenen Artikel — die Nummer des Lieferanten steht oben. */}
          <input className={`${zelle} font-mono`} placeholder={t('supplierArticleShort')} title={t('supplierArticle')} aria-label={t('supplierArticle')} {...f.artikel.props} />
        </td>
        {preise && (
          <>
            <td className="px-1.5 py-1">
              <GeldFeldFuer feld={f.ep.props} stellen={4} className={zelle} aria-label={t('unitPrice')} />
            </td>
            <td className="px-1.5 py-1">
              <input className={`${zelle} text-right tabular-nums`} inputMode="numeric" aria-label={t('priceUnit')} {...f.pe.props} />
            </td>
            <td className="px-1.5 py-1">
              <input className={`${zelle} text-right tabular-nums`} inputMode="decimal" aria-label={t('discount')} {...f.rabatt.props} />
            </td>
            <td className="px-2 py-1 text-right tabular-nums">
              <span className={betragWert}>{betrag !== null ? <span className="font-semibold">{geldText(betrag)}</span> : ''}</span>
            </td>
          </>
        )}
        <td className="px-1.5 py-1">
          {bestaetigung ? (
            <input type="date" className={`${zelle} text-xs ${datumOhneVorlage(f.termin.props.value)}`} aria-label={t('deliveryAt')} {...f.termin.props} />
          ) : (
            <input className={`${zelle} text-right tabular-nums`} inputMode="numeric" placeholder={t('daysPlaceholder')} title={t('leadTime')} aria-label={t('leadTime')} {...f.lieferzeit.props} />
          )}
        </td>
        <td className="whitespace-nowrap px-1.5 py-2 text-right">
          <span className="inline-flex items-center gap-1">
            <FeldStatusZeichen status={status} />
            {darf && (
              <button type="button" className="p-0.5 text-muted-foreground hover:text-destructive" title={t('extraRemove')} aria-label={t('extraRemove')} onClick={onLoeschen}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </span>
        </td>
      </tr>
      {preise && zeile.suggested && <GelesenZeile kind={kind} v={zeile.suggested} menge={f.menge.wert ?? zeile.quantity ?? 0} einheit={zeile.unitCode} neu preise={preise} haken={haken} darf={darf} onHaken={onHaken} />}
    </>
  )
}

/**
 * PE-107 — **was die KI gelesen hat**, als eigene Zeile unter der Antwort: die Werte in denselben Spalten, in der
 * Bezeichnung das Zitat mit Seite und Sicherheit, vorn das Kästchen für „Bestätigen". Violett, gelb bei
 * Sicherheit „mittel" oder „niedrig" (wie im Posteingang, AP-39 Nachtrag 1).
 */
function GelesenZeile({
  kind,
  v,
  menge,
  einheit,
  neu = false,
  preise,
  haken,
  darf,
  onHaken,
}: {
  kind: DocumentResponseKind
  v: DocumentResponseSuggestion
  menge: number
  einheit: string | null
  neu?: boolean
  preise: boolean
  haken: boolean
  darf: boolean
  onHaken: (an: boolean) => void
}) {
  const t = useTexte(kind)
  const bestaetigung = kind === 'CONFIRMATION'
  const unsicher = !!v.confidence && v.confidence !== 'hoch'
  const pe = v.priceUnit ?? 1
  const andereEinheit = !bestaetigung && !!v.answerUnitCode && v.answerUnitCode !== einheit
  const betrag = !v.notOffered && v.unitPrice != null && !andereEinheit ? (v.quantity ?? menge) * antwortNetto(v.unitPrice, pe, v.discountPercent) : null
  const zitat = v.offerText ? `„${v.offerText}"${v.page ? `, ${t('page', { page: v.page })}` : ''}${v.quoteFound === false ? ` (${t('quoteMissing')})` : ''}` : null
  const zelleText = 'block min-h-[calc(1.75rem+2px)] border border-transparent py-1'
  return (
    <tr className={`border-b border-border/60 align-top text-xs italic ${unsicher ? kiGelb : GELESEN.zeile}`} title={t('readRow')} data-gelesen>
      <Rand farbe={GELESEN.rand} />
      <td className="px-1.5 py-1 not-italic">
        <input type="checkbox" className="ml-4 mt-1.5" aria-label={t('readRow')} checked={haken} disabled={!darf} onChange={(e) => onHaken(e.target.checked)} />
      </td>
      <td className={`px-1.5 py-1 not-italic ${kiFarbe}`}>
        <span className="flex min-h-[calc(1.75rem+2px)] items-center gap-1 border border-transparent">
          <Sparkles className="h-3 w-3" aria-hidden />
          {t('typeRead')}
        </span>
      </td>
      <td className="px-1.5 py-1 text-right tabular-nums">
        <span className={zelleText}>{v.quantity != null ? mengeText(v.quantity) : ''}</span>
      </td>
      <td className="px-1.5 py-1">
        <span className={zelleText}>{andereEinheit ? v.answerUnitCode : ''}</span>
      </td>
      <td className="px-1.5 py-1">
        <span className={zelleText}>
          {neu && <span className="not-italic text-muted-foreground">{t('newLine')} · </span>}
          {v.notOffered && <span className="font-medium not-italic">{t('notOffered')}</span>}
          {v.alternative && (
            <span>
              {v.notOffered ? ' · ' : ''}
              {t('alternative')}: {v.alternative}
            </span>
          )}
          {zitat && <span className="text-muted-foreground"> {zitat}</span>}
          {/* Gewichtseinheit im Angebot, aber ohne Gewicht an der Zeile — der Preis gilt so nicht je Einheit der Anfrage. */}
          {v.unitHint && <span className="text-amber-700 dark:text-amber-300"> · {t('unitHint', { unit: v.unitHint })}</span>}
          {v.confidence && <span className={`ml-1 rounded px-1.5 not-italic ${SICHER_KLASSE[v.confidence]}`}>{v.confidence}</span>}
        </span>
      </td>
      <td className="px-1.5 py-1 font-mono">
        <span className={zelleText}>{v.articleNumber ?? ''}</span>
      </td>
      {preise && (
        <>
          <td className="px-1.5 py-1 text-right tabular-nums">
            <span className={zelleText}>
              {v.notOffered ? '' : v.unitPrice != null ? preisText(v.unitPrice) : t('noPrice')}
              {andereEinheit ? ` ${t('per')} ${v.answerUnitCode}` : ''}
            </span>
          </td>
          <td className="px-1.5 py-1 text-right tabular-nums">
            <span className={zelleText}>{v.notOffered || v.unitPrice == null ? '' : pe.toLocaleString('de-DE')}</span>
          </td>
          <td className="px-1.5 py-1 text-right tabular-nums">
            <span className={zelleText}>{v.discountPercent ? v.discountPercent.toLocaleString('de-DE') : ''}</span>
          </td>
          <td className="px-2 py-1 text-right tabular-nums">
            <span className={zelleText}>{betrag !== null ? geldText(betrag) : ''}</span>
          </td>
        </>
      )}
      <td className="px-1.5 py-1 text-right tabular-nums">
        <span className={zelleText}>{bestaetigung ? (v.deliveryAt ? fmtDate(v.deliveryAt) : '') : v.leadTimeDays != null ? t('days', { count: v.leadTimeDays }) : ''}</span>
      </td>
      <td />
    </tr>
  )
}
